import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RefreshCw, MessageCircle, User, Calendar, Hash, X, ArrowLeft } from 'lucide-react';
import { 
  discussionMaintenanceApiService, 
  AllDiscussion 
} from '../services/discussionMaintenanceApi';
import api from '../services/api';

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

const AllDiscussionsPanel: React.FC = () => {
  const [discussions, setDiscussions] = useState<AllDiscussion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [filter, setFilter] = useState<'all' | 'active' | 'closed'>('all');
  const [selectedDiscussion, setSelectedDiscussion] = useState<AllDiscussion | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);

  useEffect(() => {
    loadDiscussions();
  }, []);

  const loadDiscussions = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await discussionMaintenanceApiService.getAllDiscussions();
      setDiscussions(data);
    } catch (err: any) {
      console.error('Erreur chargement discussions:', err);
      setError(err.message || 'Erreur lors du chargement des discussions');
    } finally {
      setLoading(false);
    }
  };

  const loadMessages = async (discussionId: string) => {
    try {
      setLoadingMessages(true);
      const response = await api.get(`/api/discussions/${discussionId}/messages/`);
      console.log('Messages reçus:', response.data);
      // Le backend retourne un objet avec messages à l'intérieur
      const messagesData = response.data.messages || [];
      // Mapper les champs created_at vers timestamp
      const formattedMessages = messagesData.map((msg: any) => ({
        id: msg.id,
        role: msg.role,
        content: msg.content,
        timestamp: msg.created_at
      }));
      setMessages(formattedMessages);
    } catch (err: any) {
      console.error('Erreur chargement messages:', err);
      setError('Erreur lors du chargement des messages');
      setMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleDiscussionClick = async (discussion: AllDiscussion) => {
    setSelectedDiscussion(discussion);
    await loadMessages(discussion.id);
  };

  const handleCloseDiscussion = () => {
    setSelectedDiscussion(null);
    setMessages([]);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredDiscussions = discussions.filter(d => {
    if (filter === 'all') return true;
    return d.status === filter;
  });

  const stats = {
    total: discussions.length,
    active: discussions.filter(d => d.status === 'active').length,
    closed: discussions.filter(d => d.status === 'closed').length,
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center space-x-3">
          <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
          <span>Chargement des discussions...</span>
        </div>
      </div>
    );
  }

  // Si une discussion est sélectionnée, afficher ses messages
  if (selectedDiscussion) {
    return (
      <div>
        {/* Header de la discussion */}
        <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <button
              onClick={handleCloseDiscussion}
              className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors font-medium"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Retour à la liste</span>
            </button>
            <div>
              <h2 className="text-xl font-bold text-gray-900">{selectedDiscussion.title}</h2>
              <div className="flex items-center space-x-4 text-sm text-gray-600 mt-1">
                <span className="flex items-center space-x-1">
                  <User className="w-4 h-4" />
                  <span>{selectedDiscussion.user_email}</span>
                </span>
                <span className="flex items-center space-x-1">
                  <MessageCircle className="w-4 h-4" />
                  <span>{messages.length} messages</span>
                </span>
                <span className="flex items-center space-x-1">
                  <Hash className="w-4 h-4" />
                  <span className="font-mono text-xs">{selectedDiscussion.id}</span>
                </span>
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                  selectedDiscussion.status === 'active'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {selectedDiscussion.status === 'active' ? 'Active' : 'Fermée'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Messages */}
        {loadingMessages ? (
          <div className="flex items-center justify-center py-12">
            <RefreshCw className="w-6 h-6 animate-spin text-blue-600" />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-12">
            <MessageCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">Aucun message dans cette discussion</p>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`max-w-[70%] rounded-lg p-4 ${
                  message.role === 'user'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 text-gray-900'
                }`}>
                  <div className="text-sm mb-1 opacity-75">
                    {message.role === 'user' ? 'Utilisateur' : 'Iris'}
                  </div>
                  <div className="whitespace-pre-wrap">{message.content}</div>
                  <div className="text-xs mt-2 opacity-75">
                    {formatDate(message.timestamp)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      {/* Statistiques */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-blue-50 rounded-xl p-6 text-center">
          <MessageCircle className="w-8 h-8 text-blue-600 mx-auto mb-2" />
          <div className="text-2xl font-bold text-blue-900">{stats.total}</div>
          <div className="text-blue-700">Total Discussions</div>
        </div>
        
        <div className="bg-green-50 rounded-xl p-6 text-center">
          <MessageCircle className="w-8 h-8 text-green-600 mx-auto mb-2" />
          <div className="text-2xl font-bold text-green-900">{stats.active}</div>
          <div className="text-green-700">Discussions Actives</div>
        </div>
        
        <div className="bg-gray-50 rounded-xl p-6 text-center">
          <MessageCircle className="w-8 h-8 text-gray-600 mx-auto mb-2" />
          <div className="text-2xl font-bold text-gray-900">{stats.closed}</div>
          <div className="text-gray-700">Discussions Fermées</div>
        </div>
      </div>

      {/* Filtres */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex space-x-2">
          <button
            onClick={() => setFilter('all')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              filter === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Toutes ({stats.total})
          </button>
          <button
            onClick={() => setFilter('active')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              filter === 'active'
                ? 'bg-green-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Actives ({stats.active})
          </button>
          <button
            onClick={() => setFilter('closed')}
            className={`px-4 py-2 rounded-lg transition-colors ${
              filter === 'closed'
                ? 'bg-gray-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Fermées ({stats.closed})
          </button>
        </div>

        <button
          onClick={loadDiscussions}
          className="flex items-center space-x-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          <span>Actualiser</span>
        </button>
      </div>

      {/* Messages d'erreur */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Liste des discussions */}
      {filteredDiscussions.length === 0 ? (
        <div className="text-center py-12">
          <MessageCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Aucune discussion</h3>
          <p className="text-gray-600">
            {filter === 'all' 
              ? 'Aucune discussion IA pour le moment.'
              : `Aucune discussion ${filter === 'active' ? 'active' : 'fermée'}.`
            }
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredDiscussions.map((discussion) => (
            <motion.div
              key={discussion.id}
              layout
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              onClick={() => handleDiscussionClick(discussion)}
              className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer hover:border-blue-300"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="font-semibold text-gray-900">{discussion.title}</h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      discussion.status === 'active'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {discussion.status === 'active' ? 'Active' : 'Fermée'}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                    <div className="flex items-center space-x-2">
                      <Hash className="w-4 h-4" />
                      <span className="font-mono text-xs">{discussion.id.substring(0, 8)}...</span>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <User className="w-4 h-4" />
                      <span>{discussion.user_email}</span>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <MessageCircle className="w-4 h-4" />
                      <span>{discussion.message_count} messages</span>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <Calendar className="w-4 h-4" />
                      <span>{formatDate(discussion.created_at)}</span>
                    </div>
                  </div>

                  {discussion.last_user_message_time && (
                    <div className="mt-2 text-xs text-gray-500">
                      Dernier message : {formatDate(discussion.last_user_message_time)}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AllDiscussionsPanel;
